import { LockedBy, Unlock } from "../../components/locking/ConfigAppLocks";
import { compareModelKey } from "../../decorators/StoreIdEquality";
import {
  getObjectStoreIdString,
  stringifyKey,
} from "../../decorators/StoreIdHelper";
import { AcceleratorDocStoreKey } from "../../models/AcceleratorDoc";
import Model from "../../models/Model";
import { SaveModelMessages } from "../../models/messages/Messages";
import eventEmitter from "../../state/EventEmitter";
import { AcceleratorDocMetadataStore } from "../../stores/impl/AcceleratorDocMetadataStore";
import { AcceleratorDocModelStore } from "../../stores/impl/AcceleratorDocModelStore";
import { AcceleratorDocStore } from "../../stores/impl/AcceleratorDocStore";
import { ModelSaveResult, ModelStore } from "../../stores/impl/ModelStore";
import { DialogBoxConfirmEvent } from "../../types/DialogBoxEvents";

import CrudState from "../../state/CrudState";

export { SaveModel, Result as SaveModelResult };

type ModelSaveData = {
  model: Model;
  batchInstanceUuid: string;
  documentId: string;
};

type Result = {
  result: ModelSaveResult;
  modelSaveData: ModelSaveData;
};
type ModelSaveNotificationFn = (total: number) => void;

class SaveModel {
  private docStore: typeof AcceleratorDocStore;
  private docModelStore: typeof AcceleratorDocModelStore;
  private modelStore: typeof ModelStore;
  private docMetadataStore: typeof AcceleratorDocMetadataStore;
  private username: string | undefined;

  constructor(
    docStore: typeof AcceleratorDocStore,
    docModelStore: typeof AcceleratorDocModelStore,
    modelStore: typeof ModelStore,
    docMetadataStore: typeof AcceleratorDocMetadataStore,
    username: string | undefined,
  ) {
    this.docStore = docStore;
    this.docModelStore = docModelStore;
    this.modelStore = modelStore;
    this.docMetadataStore = docMetadataStore;
    this.username = username;
  }

  public async run(
    onModelSave: ModelSaveNotificationFn,
    unlock: Unlock,
    lockedBy: LockedBy,
    reprocessAffected: boolean,
    docKey: AcceleratorDocStoreKey,
  ): Promise<Result[]> {
    const documents = this.docStore.getAll();
    const models = documents.map((d) => this.docModelStore.getModel(d));

    const modelSaveData = documents.map((acceleratorDoc, index) => {
      const model = models[index];
      if (!model) {
        return;
      }
      return {
        model,
        batchInstanceUuid:
          model._firstModified?.batchInstanceUuid ??
          acceleratorDoc.storeId.batchInstanceUuid,
        documentId: model._firstModified?.docId,
      } as ModelSaveData;
    });

    // save all models
    const changedModelsUnique = modelSaveData
      .filter((x): x is ModelSaveData => x != null)
      .filter((x) => x.model.changed)
      .filter((x) => {
        const isLocked = lockedBy(x.model.uuid ?? "", "");
        const process: boolean = isLocked ? isLocked == this.username : true;
        console.log(
          `Saving=${process} ${stringifyKey(x.model.storeId)} when locked by ${lockedBy}, current user ${this.username}`,
        );
        return process;
      })
      .filter((x) => !x.model.unknown)
      .filter((x) => x.model.storeId.name !== "")
      .filter((x) => x.model.docType !== "Unknown")
      .reduce((prev: ModelSaveData[], curr: ModelSaveData) => {
        if (
          prev.filter((x) =>
            compareModelKey(x.model.storeId, curr.model.storeId),
          ).length == 0
        ) {
          return prev.concat(curr);
        }
        return prev;
      }, []);

    // set target to (numberOfModelsToSave*2) + uniqueBatches.
    // this lets us update the current when model is saved then reloaded,
    if (!changedModelsUnique.length) {
      return [];
    }
    onModelSave(changedModelsUnique.length);
    // trigger save request associated model save data, reload model as well
    const modelSavePromises = changedModelsUnique.map((x) => ({
      promise: this.modelStore.saveNew(
        x.model,
        x.batchInstanceUuid,
        x.documentId,
      ),
      modelSaveData: x,
    }));
    // update statusPromiseCount as each promise resolves
    modelSavePromises.map((x) =>
      x.promise.then((val) => {
        onModelSave(-1);
        return val;
      }),
    );
    // wait for each save to complete
    const resultsPromise = await Promise.all(
      modelSavePromises.map((x) => x.promise),
    );

    // mark the Models that were saved correctly as notChanged, update all Documents in the reverseMap
    const modelSaveResults = modelSavePromises.map(
      (x, index) =>
        ({
          result: resultsPromise[index],
          modelSaveData: x.modelSaveData,
        }) as Result,
    );

    const unlockPromises = modelSaveResults
      .filter((x) => x.result.successful && x.result.model)
      .map((x) =>
        unlock(x.result.model!.uuid!, x.result.model!.batchClassIdentifier, ""),
      );

    modelSaveResults.forEach((reloaded) => {
      if (reloaded.result.model != null) {
        // update each document to reference reloaded Model after save
        const newModel = new Model(reloaded.result.model);
        newModel.setChanged(false);
        this.modelStore.set(newModel, true);
        CrudState.updateCachedModel(newModel);
        try {
          const oldModel = reloaded.modelSaveData.model;
          const accDocKeysOld = this.docModelStore.getDocuments(oldModel);
          const accDocKeysNew = this.docModelStore.getDocuments(newModel);
          const allKeysMap = new Map<string, AcceleratorDocStoreKey>();
          [...accDocKeysOld, ...accDocKeysNew].forEach((k) => {
            allKeysMap.set(stringifyKey(k), k);
          });
          const accDocKeys = Array.from(allKeysMap.values());

          // re-associate so AcceleratorDocModelStore's map & classifiedModelName sync stays the
          // single source of truth for the doc/metadata <-> Model name relationship
          accDocKeys.forEach((key) => this.docModelStore.associate(key, newModel));
          const acceleratorDocMetadatas =
            this.docMetadataStore.getManyWithoutLookup(accDocKeys);
          acceleratorDocMetadatas.forEach((docMeta) => {
            if (docMeta) {
              docMeta.setModelChanged(false);
              docMeta.setClassifiedModelName(newModel.getName());
            }
          });
          accDocKeys.forEach((key) => {
            const accDoc = this.docStore.getWithoutLookup(key);
            if (accDoc) {
              accDoc.hasModelChanged = false;
              accDoc.setModelName(newModel.getName());
              accDoc.classifiedModel = newModel.toModelData();
            }
          });
        } catch (e) {
          console.error(
            `SaveModel error attempting to get AcceleratorDocs for Model ${getObjectStoreIdString(newModel)}`,
            e,
          );
        }
      }
      onModelSave(-1);
    });

    await Promise.all(unlockPromises);

    if (reprocessAffected) {
      const affectedBatches = modelSaveResults
        .map((x) => x.modelSaveData.batchInstanceUuid)
        .concat(modelSaveResults.flatMap((x) => x.result.affectedBatches))
        .concat(docKey.batchInstanceUuid)
        .filter((x) => x != undefined && x !== "")
        .filter((x, i, a) => a.indexOf(x) === i);
      console.debug(
        `Reprocessing ${affectedBatches.length} affected batches enabled`,
      );
    }

    return modelSaveResults;
  }
}
